import * as React from "react";
import styled from "styled-components";
import { EditableUpdate, Update, ValueType } from ".";
import Checkbox, { CheckboxStates } from "../common/Checkbox";

interface Props {
  customers: EditableUpdate[];
  onSetFieldOverwrite: (
    customer: EditableUpdate,
    newOverwriteFlags: ValueType
  ) => void;
}

const Table = styled.table`
  & thead tr {
    & > *:nth-child(2) {
      width: 300px;
    }
  }

  // Right align text in the first column
  & > * > tr > * {
    white-space: pre-wrap;

    &:nth-child(1) {
      text-align: right;
      font-weight: bold;
      width: 150px; // Fit column to content size
    }

    &:nth-child(4) {
      text-align: right;
      width: 130px;
    }

    &:last-child {
      padding-right: 10px;
    }
  }
`;

interface RowProps extends React.PropsWithChildren {
  customer: EditableUpdate;
  valueType: ValueType;
  onSetFieldOverwrite: (
    customer: EditableUpdate,
    newOverwriteFlags: ValueType
  ) => void;
}

const Row = ({
  children,
  customer,
  valueType,
  onSetFieldOverwrite,
}: RowProps) => {
  function hasChanges(customer: Update, type: ValueType): boolean {
    return (customer.changes & type) == type;
  }

  function hasConflicts(customer: Update, type: ValueType): boolean {
    return (customer.conflicts & type) == type;
  }

  function hasOverwriteFlag(
    customer: EditableUpdate,
    type: ValueType
  ): boolean {
    return (customer.overwrites & type) != 0;
  }

  function onFieldOverwriteChanged(
    customer: EditableUpdate,
    type: ValueType,
    overwrite: boolean
  ) {
    let flags = customer.overwrites;

    if (overwrite) {
      flags |= type;
    } else {
      flags &= ~type;
    }

    onSetFieldOverwrite(customer, flags);
  }

  if (!hasChanges(customer, valueType)) {
    return null;
  }

  return (
    <tr>
      {children}
      {hasConflicts(customer, valueType) && (
        <td>
          <input
            type="checkbox"
            checked={hasOverwriteFlag(customer, valueType)}
            onChange={(ev) =>
              onFieldOverwriteChanged(customer, valueType, ev.target.checked)
            }
          />
        </td>
      )}
    </tr>
  );
};

const ExistingCustomerList = ({ customers, onSetFieldOverwrite }: Props) => {
  const onOverwriteAllChanged = (
    customer: EditableUpdate,
    ev: React.ChangeEvent<HTMLInputElement>
  ) => {
    if (ev.target.checked) {
      onSetFieldOverwrite(customer, customer.conflicts);
    } else {
      onSetFieldOverwrite(customer, ValueType.None);
    }
  };

  const getOverwriteAllState = (customer: EditableUpdate): CheckboxStates => {
    if ((customer.overwrites & customer.conflicts) == customer.conflicts) {
      return CheckboxStates.Checked;
    } else if (customer.overwrites == 0) {
      return CheckboxStates.Unchecked;
    }

    return CheckboxStates.Indeterminate;
  };

  return (
    <div>
      {!customers.length && <p>Inga kunder med nya värden hittades.</p>}
      {customers.map((customer) => (
        <div key={customer.new.customerNo}>
          <h4>{customer.current.name}</h4>
          <Table className="table-small dataTable dtr-inline">
            <thead>
              <tr>
                <th>Fält</th>
                <th>Nuvarande värde</th>
                <th>Nytt värde</th>
                {customer.conflicts != ValueType.None && (
                  <th>
                    <label>
                      <strong>Skriv över</strong>
                      &nbsp;
                      <Checkbox
                        onChange={(ev) => onOverwriteAllChanged(customer, ev)}
                        state={getOverwriteAllState(customer)}
                      />
                    </label>
                  </th>
                )}
              </tr>
            </thead>
            <tbody>
              <Row
                customer={customer}
                valueType={ValueType.OrganizationNumber}
                onSetFieldOverwrite={onSetFieldOverwrite}
              >
                <td>Organisationsnummer</td>
                <td>{customer.current.organizationNumber}</td>
                <td>{customer.new.organizationNumber}</td>
              </Row>
              <Row
                customer={customer}
                valueType={ValueType.Name}
                onSetFieldOverwrite={onSetFieldOverwrite}
              >
                <td>Namn</td>
                <td>{customer.current.name}</td>
                <td>{customer.new.name}</td>
              </Row>
              <Row
                customer={customer}
                valueType={ValueType.Address1}
                onSetFieldOverwrite={onSetFieldOverwrite}
              >
                <td>Address 1</td>
                <td>{customer.current.address1}</td>
                <td>{customer.new.address1}</td>
              </Row>
              <Row
                customer={customer}
                valueType={ValueType.Address2}
                onSetFieldOverwrite={onSetFieldOverwrite}
              >
                <td>Address 2</td>
                <td>{customer.current.address2}</td>
                <td>{customer.new.address2}</td>
              </Row>
              <Row
                customer={customer}
                valueType={ValueType.ZipCode}
                onSetFieldOverwrite={onSetFieldOverwrite}
              >
                <td>Postnr</td>
                <td>{customer.current.zipCode}</td>
                <td>{customer.new.zipCode}</td>
              </Row>
              <Row
                customer={customer}
                valueType={ValueType.Town}
                onSetFieldOverwrite={onSetFieldOverwrite}
              >
                <td>Ort</td>
                <td>{customer.current.town}</td>
                <td>{customer.new.town}</td>
              </Row>
              <Row
                customer={customer}
                valueType={ValueType.ContactPhone}
                onSetFieldOverwrite={onSetFieldOverwrite}
              >
                <td>Kontakttelefon</td>
                <td>{customer.current.contactPhone}</td>
                <td>{customer.new.contactPhone}</td>
              </Row>
              <Row
                customer={customer}
                valueType={ValueType.ContactPerson}
                onSetFieldOverwrite={onSetFieldOverwrite}
              >
                <td>Kontaktperson</td>
                <td>{customer.current.contactPerson}</td>
                <td>{customer.new.contactPerson}</td>
              </Row>
              <Row
                customer={customer}
                valueType={ValueType.Email}
                onSetFieldOverwrite={onSetFieldOverwrite}
              >
                <td>E-post</td>
                <td>{customer.current.email}</td>
                <td>{customer.new.email}</td>
              </Row>
              <Row
                customer={customer}
                valueType={ValueType.Reference}
                onSetFieldOverwrite={onSetFieldOverwrite}
              >
                <td>Referens</td>
                <td>{customer.current.reference}</td>
                <td>{customer.new.reference}</td>
              </Row>
            </tbody>
          </Table>
        </div>
      ))}
    </div>
  );
};

export default ExistingCustomerList;
